import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  AnalysisContent,
  AnalysisSectionThemeProvider,
  SectionContentType,
  AnalysisDemographicsTab,
  AnalysisEmploymentTab,
  AnalysisTaxTab,
  AnalysisElectricityTab,
  AnalysisTransportAccessTab,
  AnalysisAmenitiesTab,
  AnalysisPurchaseMarketTab,
  AnalysisRentalMarketTab,
  AnalysisBuildingPermitsTab,
  AnalysisTransactionsTab,
  AnalysisListingsTab,
  AnalysisNoiseTab,
  AnalysisSunlightTab,
  SectionNotAvailable,
} from '@popety-io/ui'
import { SummaryTab } from './SummaryTab'
import { AutoValuationsTab } from './AutoValuations'
import { ManualValuationsTab } from './ManualValuationsTab'
import { PropertyDetailsTab } from './PropertyDetailsTab'
// ComparablesTab, BuildingPermitsTab, NoiseTab, SunlightTab now shared from @popety-io/ui
import { HistoryTab } from './HistoryTab'
import { LandTab } from './LandTab'
import { BuildingTab } from './BuildingTab'
import { EnergyTab } from './EnergyTab'
// TransportAccessTab now shared from @popety-io/ui
import { TabContentWrapper } from '../../../components/shared'
import {
  SharedFilesSection,
  SharedNotesSection,
} from '../../../components/SharedAttachments/SharedAttachments'
import { useMapSectionData } from '../contexts'

export interface PublicEstimateContentProps {
  activeSection: string
  estimateData: any
  hash: string
  availableSections?: string[]
}

export const PublicEstimateContent: React.FC<PublicEstimateContentProps> = ({
  activeSection,
  estimateData,
  hash,
  availableSections,
}) => {
  const { t } = useTranslation()
  const { setActiveSection, clearSectionData } = useMapSectionData()

  // Clear map section data when navigating to sections without dynamic layers
  useEffect(() => {
    const sectionsWithDynamicLayers = [
      'listings_comparables',
      'transactions',
      'amenities',
      'transport_access',
      'demographics',
      'employment',
      'purchase_market',
      'rental_market',
      'noise_analysis',
      'history',
      'sunlight',
      'energy',
      'building_permits',
    ]

    if (sectionsWithDynamicLayers.includes(activeSection)) {
      // Bridge sidebar section to page-level context so the map picks it up
      setActiveSection(activeSection as any)
    } else {
      // Clear map layers for sections without dynamic layers
      setActiveSection(null)
      clearSectionData()
    }
  }, [activeSection])

  // Check if section is accessible
  // If availableSections is undefined, all sections are accessible (backward compatibility)
  const isSectionAccessible = (sectionId: string): boolean => {
    if (!availableSections || availableSections.length === 0) {
      return true
    }
    return availableSections.includes(sectionId)
  }

  const sectionContent: Record<string, SectionContentType> = {
    // Overview section
    summary: {
      title: t('estimate.sidebar.summary'),
      icon: 'bar-chart',
      sectionColor: 'steel',
    },
    // Property section
    property_details: {
      title: t('estimate.navigation.property_details'),
      icon: 'building',
      sectionColor: 'steel',
    },
    history: {
      title: t('estimate.navigation.history'),
      icon: 'clock',
      sectionColor: 'steel',
    },
    land: {
      title: t('estimate.sidebar.land'),
      icon: 'land-plot',
      sectionColor: 'steel',
    },
    building: {
      title: t('estimate.sidebar.building'),
      icon: 'cube',
      sectionColor: 'steel',
    },
    energy: {
      title: t('estimate.sidebar.energy'),
      icon: 'battery-charging',
      sectionColor: 'deepBlue',
    },
    // Estimate section
    auto_valuations: {
      title: t('estimate.sidebar.autoValuations'),
      icon: 'sparkles',
      sectionColor: 'bronze',
    },
    manual_valuations: {
      title: t('estimate.sidebar.manualValuations'),
      icon: 'edit',
      sectionColor: 'bronze',
    },
    listings_comparables: {
      title: t('estimate.navigation.listings_comparables'),
      icon: 'tag',
      sectionColor: 'bronze',
    },
    transactions: {
      title: t('estimate.navigation.transactions_comparables'),
      icon: 'real-estate-transaction',
      sectionColor: 'bronze',
    },
    // Market section
    purchase_market: {
      title: t('estimate.navigation.purchase_market'),
      icon: 'cash',
      sectionColor: 'forest',
    },
    rental_market: {
      title: t('estimate.navigation.rental_market'),
      icon: 'home',
      sectionColor: 'forest',
    },
    // Environment section
    building_permits: {
      title: t('estimate.sidebar.buildingPermits'),
      icon: 'building-permit',
      sectionColor: 'deepBlue',
    },
    transport_access: {
      title: t('estimate.sidebar.transportAccess'),
      icon: 'car',
      sectionColor: 'deepBlue',
    },
    amenities: {
      title: t('estimate.navigation.amenities'),
      icon: 'store',
      sectionColor: 'deepBlue',
    },
    noise_analysis: {
      title: t('estimate.navigation.noise_analysis'),
      icon: 'activity',
      sectionColor: 'deepBlue',
    },
    sunlight: {
      title: t('estimate.navigation.sunlight_study'),
      icon: 'sun',
      sectionColor: 'deepBlue',
    },
    // Social & Economic section
    demographics: {
      title: t('estimate.navigation.demographics'),
      icon: 'users',
      sectionColor: 'wine',
    },
    employment: {
      title: t('estimate.sidebar.employment'),
      icon: 'briefcase',
      sectionColor: 'wine',
    },
    taxes: {
      title: t('estimate.sidebar.taxes'),
      icon: 'receipt',
      sectionColor: 'wine',
    },
    electricity: {
      title: t('estimate.sidebar.electricity'),
      icon: 'zap',
      sectionColor: 'wine',
    },
    notes: {
      title: t('common.sidebar.items.notes'),
      icon: 'edit',
      sectionColor: 'steel',
    },
    files: {
      title: t('common.sidebar.items.files'),
      icon: 'file',
      sectionColor: 'steel',
    },
  }

  // Render specific section content wrapped in scrollable container
  const renderContent = () => {
    // Check if section is accessible before rendering
    if (!isSectionAccessible(activeSection)) {
      return <SectionNotAvailable sectionName={activeSection} />
    }

    switch (activeSection) {
      // Overview section
      case 'summary':
        return <SummaryTab estimateData={estimateData} />

      // Property section
      case 'property_details':
        return <PropertyDetailsTab estimateData={estimateData} hash={hash} />

      case 'history':
        return <HistoryTab estimateData={estimateData} hash={hash} />

      case 'land':
        return <LandTab estimateData={estimateData} />

      case 'building':
        return <BuildingTab estimateData={estimateData} />

      case 'energy':
        return <EnergyTab estimateData={estimateData} />

      // Estimate section
      case 'auto_valuations':
        return <AutoValuationsTab estimateData={estimateData} />

      case 'manual_valuations':
        return <ManualValuationsTab estimateData={estimateData} />

      case 'listings_comparables':
        return <AnalysisListingsTab type="purchase" />

      case 'transactions':
        return <AnalysisTransactionsTab />

      // Market section (shared from @popety-io/ui)
      case 'purchase_market':
        return <AnalysisPurchaseMarketTab />

      case 'rental_market':
        return <AnalysisRentalMarketTab />

      // Environment section
      case 'building_permits':
        return <AnalysisBuildingPermitsTab />

      case 'transport_access':
        return <AnalysisTransportAccessTab />

      case 'amenities':
        return <AnalysisAmenitiesTab />

      case 'noise_analysis':
        return <AnalysisNoiseTab />

      case 'sunlight':
        return <AnalysisSunlightTab />

      // Social & Economic section — shared from @popety-io/ui
      case 'demographics':
        return <AnalysisDemographicsTab />

      case 'employment':
        return <AnalysisEmploymentTab />

      case 'taxes':
        return <AnalysisTaxTab />

      case 'electricity':
        return <AnalysisElectricityTab />

      case 'notes':
        return <SharedNotesSection notes={estimateData?.notes} />

      case 'files':
        return (
          <SharedFilesSection
            files={estimateData?.files}
            hash={hash}
            kind="estimate"
          />
        )

      default:
        // For other sections, render placeholder content using AnalysisContent
        return (
          <AnalysisContent
            activeSection={activeSection}
            sectionContent={sectionContent}
          />
        )
    }
  }

  const section = sectionContent[activeSection]
  const sectionTheme = useMemo(
    () => ({ icon: section?.icon, sectionColor: section?.sectionColor }),
    [section?.icon, section?.sectionColor]
  )

  return (
    <AnalysisSectionThemeProvider value={sectionTheme}>
      <TabContentWrapper>{renderContent()}</TabContentWrapper>
    </AnalysisSectionThemeProvider>
  )
}
